import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import logger from './utils/logger';
import schoolListData from './assets/school_list.json';
import IeTandCs from './ie_TandCs';
import UkTandCs from './uk_TandCs';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: '',  
    teacher_email: '',
    teacher_subject: 'English', // Default subject
    teacher_code: '',
    emailUpdates: true,
    country: '',
    school_name: '',
    referral_code: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTeacherField, setShowTeacherField] = useState(false);
  const [schoolList, setSchoolList] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [teacherInputMode, setTeacherInputMode] = useState('email'); // 'email' or 'code'
  const schoolDropdownRef = useRef(null);
  const [showTandCs, setShowTandCs] = useState(false);
  const [tandCsCountry, setTandCsCountry] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [howHeardOther, setHowHeardOther] = useState('');
  
  // List of available subjects
  const subjects = ["English", "Irish", "French", "Spanish", "German", "Maths"];

  // Load school list
  useEffect(() => {
    try {
      // Use the imported JSON directly 
      setSchoolList(schoolListData);
      logger.debug('School list loaded:', schoolListData.slice(0, 2));
    } catch (error) {
      logger.error("Error loading school list:", error);
      setSchoolList([]);
    }
  }, []);

  // Handle clicking outside of the school dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (schoolDropdownRef.current && !schoolDropdownRef.current.contains(event.target)) {
        setShowSchoolDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Function to normalize text by removing accents
  const normalizeText = (text) => {
    return text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  // Helper function to check if school name contains search terms
  const matchesSearchTerms = (schoolName, searchTerms) => {
    const normalizedSchoolName = normalizeText(schoolName.toLowerCase());
    // Check if all search terms are found in the school name
    return searchTerms.every(term => 
      normalizedSchoolName.includes(term)
    );
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    
    // Show teacher field if role is student
    if (e.target.name === 'role' && e.target.value === 'student') {
      setShowTeacherField(true);
    } else if (e.target.name === 'role' && e.target.value !== 'student') {
      setShowTeacherField(false);
    }

    // Handle country selection - reset school if country changes
    if (e.target.name === 'country') {
      setFormData(prev => ({ ...prev, school_name: '' }));
    }

    // Filter schools based on input (only for Ireland)
    if (e.target.name === 'school_name' && formData.country === 'Ireland') {
      if (value.trim().length < 2) {
        // Don't show dropdown until user types at least 2 characters
        setShowSchoolDropdown(false);
        setFilteredSchools([]);
      } else {
        // Split input into words and normalize them for search
        const searchTerms = value.toLowerCase().split(/\s+/).filter(term => term.length > 0)
          .map(term => normalizeText(term));
        
        // Filter schools that match all search terms
        const filtered = schoolList.filter(school => 
          matchesSearchTerms(school, searchTerms)
        ).slice(0, 10); // Limit to 10 results for performance
        
        setFilteredSchools(filtered);
        setShowSchoolDropdown(true);
      }
    }
    
    // Process teacher code input (force uppercase and alphanumeric)
    if (e.target.name === 'teacher_code') {
      const code = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 4);
      setFormData(prev => ({ ...prev, teacher_code: code }));
    }

    // Process referral code input (force uppercase and alphanumeric)
    if (e.target.name === 'referral_code') {
      const code = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 6);
      setFormData(prev => ({ ...prev, referral_code: code }));
    }

    // Handle how_heard and how_heard_other
    if (e.target.name === 'how_heard') {
      setHowHeard(e.target.value);
      if (e.target.value !== 'Other') {
        setHowHeardOther(''); // Clear other text if not "Other"
      }
    }
  };

  const handleHowHeardChange = (e) => {
    setHowHeard(e.target.value);
    if (e.target.value !== 'Other') {
      setHowHeardOther(''); // Clear other text if not "Other"
    }
  };

  // Add a click handler for the school input to show all schools initially (only for Ireland)
  const handleSchoolInputClick = () => {
    if (formData.country !== 'Ireland') return;
    
    logger.debug('School input clicked, school list:', schoolList.slice(0, 2));
    if (schoolList && schoolList.length > 0 && formData.school_name.trim().length >= 2) {
      // Only show dropdown if there are at least 2 characters typed
      const searchTerms = formData.school_name.toLowerCase().split(/\s+/).filter(term => term.length > 0)
        .map(term => normalizeText(term));
      
      const filtered = schoolList.filter(school => 
        matchesSearchTerms(school, searchTerms)
      ).slice(0, 10);
      
      setFilteredSchools(filtered);
      setShowSchoolDropdown(true);
    }
  };

  const handleSchoolSelect = (school) => {
    setFormData({ ...formData, school_name: school });
    setShowSchoolDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (formData.password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    // Create a clean version of the form data with trimmed values and lowercase email
    const cleanFormData = {};
    for (const key in formData) {
      if (typeof formData[key] === 'string') {
        cleanFormData[key] = formData[key].trim();
      } else {
        cleanFormData[key] = formData[key];
      }
    }
    // Convert email to lowercase
    if (cleanFormData.email) {
      cleanFormData.email = cleanFormData.email.toLowerCase();
    }
    
    // Ensure referral code is uppercase before sending
    if (cleanFormData.referral_code) {
      cleanFormData.referral_code = cleanFormData.referral_code.toUpperCase();
    }
    
    // Handle teacher_email and subject
    if (cleanFormData.role === 'student') {
      if (teacherInputMode === 'email' && cleanFormData.teacher_email) {
        // Format teacher_email as a JSON object with the subject as key
        const teacherDict = {
          [cleanFormData.teacher_subject]: cleanFormData.teacher_email
        };
        cleanFormData.teacher_email_json = JSON.stringify(teacherDict);
      } else {
        cleanFormData.teacher_email_json = null;
      }
      
      // Keep teacher_code as-is if using code mode
      if (teacherInputMode !== 'code') {
        cleanFormData.teacher_code = '';
      }
    } else {
      cleanFormData.teacher_email_json = null;
      cleanFormData.teacher_code = '';
    }
    
    // Add how_heard data
    cleanFormData.how_heard = howHeard;
    if (howHeard === 'Other') {
      cleanFormData.how_heard_other = howHeardOther;
    } else {
      cleanFormData.how_heard_other = null;
    }

    logger.info("Submitting form data:", cleanFormData);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/register`, 
        cleanFormData,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          withCredentials: true
        }
      );
      alert(response.data.message + "\n\nPlease check your spam folder if you don't see the verification email.");
      window.location.href = "/";
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || "Registration failed";
      setError(errorMessage);
      if (errorMessage === "Username already exists") {
        document.getElementById("username").focus();
      } else if (errorMessage === "Email already exists") {
        document.getElementById("email").focus();
      }
    }
  };

  // Toggle between email and code input for teacher connection
  const toggleTeacherInputMode = () => {
    setTeacherInputMode(prev => prev === 'code' ? 'email' : 'code');
    // Clear the previous input when switching modes
    if (teacherInputMode === 'email') {
      setFormData(prev => ({ ...prev, teacher_email: '', teacher_code: '' }));
    } else {
      setFormData(prev => ({ ...prev, teacher_email: '', teacher_code: '' }));
    }
  };

  return (
    <div style={{ maxWidth: '400px', margin: '0 auto', fontFamily: 'sans-serif' }}>
      <h2>Register</h2>
      <div style={{ 
        fontSize: '14px',
        color: '#92400e',
        backgroundColor: '#fffbeb',
        padding: '12px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        ⚠️ After registering, please check your spam folder if you don't see the verification email.
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
        <input 
          type="text" 
          name="username" 
          id="username" 
          placeholder="Username" 
          onChange={handleChange} 
          style={{ 
            marginBottom: '10px', 
            padding: '8px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1'
          }}
          required
        />

        <input 
          type="email" 
          name="email" 
          id="email"
          placeholder="Email" 
          onChange={handleChange} 
          style={{ 
            marginBottom: '10px', 
            padding: '8px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1'
          }}
          required
        />

        <div style={{ 
          position: 'relative', 
          marginBottom: '10px'
        }}>
          <input 
            type={showPassword ? "text" : "password"}
            name="password" 
            id="password"
            placeholder="Password" 
            onChange={handleChange} 
            style={{ 
              width: '100%',
              padding: '8px',
              paddingRight: '40px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              boxSizing: 'border-box'
            }}
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#718096'
            }}
          >
            {showPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>

        <div style={{ 
          position: 'relative', 
          marginBottom: '10px'
        }}>
          <input
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{ 
              width: '100%',
              padding: '8px',
              paddingRight: '40px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              boxSizing: 'border-box'
            }}
            required
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#718096'
            }}
          >
            {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
          </button>
        </div>

        <select
          name="country"
          id="country"
          value={formData.country}
          onChange={handleChange}
          style={{ 
            marginBottom: '10px', 
            padding: '8px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            backgroundColor: 'white'
          }}
          required
        >
          <option value="">Select your country</option>
          <option value="Ireland">Ireland</option>
          <option value="United Kingdom">United Kingdom</option>
        </select>

        <div style={{ 
          position: 'relative', 
          marginBottom: '10px'
        }} ref={schoolDropdownRef}>
          <input 
            type="text" 
            name="school_name" 
            id="school_name" 
            placeholder={formData.country === 'Ireland' 
              ? "School Name (type to search)" 
              : "School Name"}
            value={formData.school_name}
            onChange={handleChange} 
            onClick={handleSchoolInputClick}
            style={{ 
              width: '100%',
              padding: '8px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              boxSizing: 'border-box'
            }}
            required
          />
          {showSchoolDropdown && formData.country === 'Ireland' && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: '0',
              right: '0',
              maxHeight: '200px',
              overflow: 'auto',
              backgroundColor: 'white',
              border: '2px solid #3182ce',
              borderRadius: '8px',
              zIndex: 10,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              {filteredSchools.length > 0 ? (
                filteredSchools.map((school, index) => (
                  <div 
                    key={index}
                    onClick={() => handleSchoolSelect(school)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: index < filteredSchools.length - 1 ? '1px solid #edf2f7' : 'none',
                      backgroundColor: '#f7fafc'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                  >
                    {school}
                  </div>
                ))
              ) : (
                formData.school_name.trim() !== '' && (
                  <div style={{
                    padding: '8px 12px',
                    color: '#718096',
                    textAlign: 'center'
                  }}>
                    No schools found matching "{formData.school_name}"
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <select
          name="role"
          id="role"
          value={formData.role}
          onChange={handleChange}
          style={{ 
            marginBottom: '10px', 
            padding: '8px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            backgroundColor: 'white'
          }}
          required
        >
          <option value="">Select your role</option>
          <option value="student">Student</option>
          <option value="teacher">Teacher</option>
          <option value="parent">Parent</option>
        </select>

        {/* "How did you hear about us?" section */}
        {formData.role && (
          <div style={{ marginBottom: '10px' }}>
            <label 
              htmlFor="how_heard"
              style={{
                display: 'block',
                marginBottom: '5px',
                fontSize: '14px',
                color: '#4a5568'
              }}
            >
              How did you hear about us? (Optional)
            </label>
            <select
              name="how_heard"
              id="how_heard"
              value={howHeard}
              onChange={handleHowHeardChange}
              style={{ 
                marginBottom: '10px', 
                padding: '8px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                backgroundColor: 'white',
                width: '100%'
              }}
            >
              <option value="">Select an option</option>
              <option value="News Media">News Media</option>
              <option value="Friend">Friend</option>
              {formData.role === 'teacher' && <option value="Colleague">Colleague</option>}
              {formData.role === 'student' && <option value="My Teacher">My Teacher</option>}
              <option value="Instagram">Instagram</option>
              <option value="Tiktok">Tiktok</option>
              <option value="Other">Other</option>
            </select>

            {howHeard === 'Other' && (
              <input
                type="text"
                name="how_heard_other"
                id="how_heard_other"
                placeholder="Please specify"
                value={howHeardOther}
                onChange={(e) => setHowHeardOther(e.target.value)}
                style={{ 
                  width: '100%',
                  padding: '8px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  boxSizing: 'border-box',
                  marginBottom: '10px'
                }}
              />
            )}
          </div>
        )}

        {/* Add teacher email and subject fields when role is student */}
        {showTeacherField && (
          <div style={{ marginBottom: '10px' }}>
            <div style={{ 
              fontSize: '14px',
              color: '#4a5568',
              marginBottom: '15px',
              textAlign: 'center',
              backgroundColor: '#f7fafc',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0'
            }}>
              Connecting with a teacher is optional - you can skip this step and add teachers later
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginBottom: '15px' 
            }}>
              <div style={{ 
                display: 'flex', 
                border: '1px solid #cbd5e1', 
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <button
                  type="button"
                  onClick={toggleTeacherInputMode}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: teacherInputMode === 'email' ? '#2980b9' : '#f8f9fa',
                    color: teacherInputMode === 'email' ? 'white' : '#333',
                    border: 'none',
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  Connect by Email
                </button>
                <button
                  type="button"
                  onClick={toggleTeacherInputMode}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: teacherInputMode === 'code' ? '#2980b9' : '#f8f9fa',
                    color: teacherInputMode === 'code' ? 'white' : '#333',
                    border: 'none',
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  Connect by Code
                </button>
              </div>
            </div>
            
            {teacherInputMode === 'email' ? (
              <div style={{ 
                display: 'flex',
                gap: '10px',
                marginBottom: '10px'
              }}>
                <select
                  name="teacher_subject"
                  id="teacher_subject"
                  value={formData.teacher_subject}
                  onChange={handleChange}
                  style={{ 
                    flex: '1',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: 'white'
                  }}
                >
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
                
                <input 
                  type="email" 
                  name="teacher_email" 
                  id="teacher_email" 
                  placeholder="Teacher's Email (optional)" 
                  value={formData.teacher_email}
                  onChange={handleChange} 
                  style={{ 
                    flex: '2',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1'
                  }}
                />
              </div>
            ) : (
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <select
                  name="teacher_subject"
                  id="teacher_subject"
                  value={formData.teacher_subject}
                  onChange={handleChange}
                  style={{ 
                    width: '100%',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: 'white',
                    marginBottom: '10px'
                  }}
                >
                  {subjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
                
                <input 
                  type="text" 
                  name="teacher_code" 
                  id="teacher_code" 
                  placeholder="Teacher's 4-digit Code" 
                  value={formData.teacher_code}
                  onChange={handleChange} 
                  style={{ 
                    width: '100%',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    textAlign: 'center',
                    fontSize: '18px',
                    letterSpacing: '5px',
                    textTransform: 'uppercase'
                  }}
                  maxLength="4"
                />
              </div>
            )}
            
            <div style={{ 
              fontSize: '12px',
              color: '#718096',
              marginBottom: '10px'
            }}>
              You can add more teachers later in your account settings
            </div>
          </div>
        )}

        <div style={{ 
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <input
            type="checkbox"
            name="emailUpdates"
            id="emailUpdates"
            checked={formData.emailUpdates}
            onChange={handleChange}
            style={{ 
              width: '16px',
              height: '16px'
            }}
          />
          <label 
            htmlFor="emailUpdates"
            style={{
              fontSize: '14px',
              color: '#4a5568'
            }}
          >
            Email me about new subjects and features
          </label>
        </div>

        {formData.country && (
          <div style={{ 
            marginBottom: '20px',
            fontSize: '14px',
            color: '#64748b',
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'center'
          }}>
            <input
              type="checkbox"
              name="agreeTandCs"
              id="agreeTandCs"
              required
              style={{ width: '16px', height: '16px' }}
            />
            <label htmlFor="agreeTandCs" style={{ fontSize: '14px', color: '#4a5568' }}>
              I agree to the{' '}
              <button
                type="button"
                style={{ color: '#2980b9', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                onClick={() => {
                  setShowTandCs(true);
                  setTandCsCountry(formData.country);
                }}
              >
                Terms and Conditions
              </button>
            </label>
          </div>
        )}

        {showTandCs && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              background: 'white',
              maxWidth: 900,
              maxHeight: '90vh',
              overflowY: 'auto',
              borderRadius: 8,
              padding: 24,
              position: 'relative',
              boxShadow: '0 4px 32px rgba(0,0,0,0.2)'
            }}>
              <button
                type="button"
                onClick={() => setShowTandCs(false)}
                style={{
                  position: 'sticky',
                  top: 0,
                  float: 'right',
                  background: 'none',
                  border: 'none',
                  fontSize: 24,
                  cursor: 'pointer',
                  color: '#888',
                  zIndex: 10,
                  marginLeft: 'auto',
                  display: 'block'
                }}
                aria-label="Close Terms and Conditions"
              >
                ×
              </button>
              {tandCsCountry === 'Ireland' && <IeTandCs />}
              {tandCsCountry === 'United Kingdom' && <UkTandCs />}
            </div>
          </div>
        )}

        <div style={{ 
          marginBottom: '20px',
          fontSize: '14px',
          color: '#64748b',
          textAlign: 'center'
        }}>
          By registering, you agree to our{' '}
          <Link 
            to="/privacy"
            style={{
              color: '#2980b9',
              textDecoration: 'none'
            }}
          >
            Privacy Policy
          </Link>
        </div>

        {error && (
          <div style={{ 
            color: '#e53e3e', 
            backgroundColor: '#fff5f5',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <button 
          type="submit" 
          style={{ 
            padding: '10px',
            backgroundColor: '#2980b9',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            marginBottom: '10px',
            width: '100%'
          }}
        >
          Register
        </button>

        <Link 
          to="/"
          style={{ 
            display: 'block',
            padding: '10px',
            backgroundColor: '#95a5a6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            textDecoration: 'none',
            textAlign: 'center',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          Back to Login
        </Link>
      </form>
    </div>
  );
};

export default Register;